import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useLesson, updateLesson } from '@/hooks/useLesson';
import { LessonFormFields } from '@/components/admin/lesson/LessonFormFields';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { toast } from '@/components/ui/use-toast';
import type { LessonFormData, QuizQuestion } from '@/components/admin/lesson/types';
import { Loader2 } from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';

const AdminEditLesson: FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: lessonData, isLoading, error } = useLesson(lessonId);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);

  const form = useForm<LessonFormData>({
    defaultValues: {
      title: '',
      video_url: '',
      audio_url: '',
      content_text: '',
      level: 'principiante',
      published: false,
      is_premium: false,
      quiz_questions: [],
    }
  });

  useEffect(() => {
    if (lessonData) {
      console.log('AdminEditLesson - Cargando datos de la lección:', lessonData);
      
      // Asegurarse de que las preguntas tengan el formato correcto
      const formattedQuestions = lessonData.quiz_questions?.map(q => ({
        ...q,
        wrong_answers: Array.isArray(q.wrong_answers) ? q.wrong_answers : ['', '', '']
      })) || [];

      console.log('AdminEditLesson - Preguntas formateadas:', formattedQuestions);
      
      // Actualizar el estado local de preguntas
      setQuestions(formattedQuestions);
      
      // Actualizar el formulario con todos los datos
      form.reset({
        ...lessonData,
        quiz_questions: formattedQuestions
      });
    }
  }, [lessonData, form]);

  // Si no hay lessonId, redirigir a la lista
  useEffect(() => {
    if (!lessonId) {
      console.error('No lesson ID provided');
      toast({
        title: "Error",
        description: "No se encontró la lección",
        variant: "destructive",
      });
      navigate('/admin/lessons');
    }
  }, [lessonId, navigate]);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !lessonData) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <h1 className="text-2xl font-bold text-red-600">Error al cargar la lección</h1>
          <p className="text-gray-600">{error?.message || 'No se pudo cargar la lección'}</p>
          <Button onClick={() => navigate('/admin/lessons')}>
            Volver a la lista
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const onSubmit = async (data: LessonFormData) => {
    if (!lessonId) return;
    
    try {
      console.log('AdminEditLesson - Enviando datos del formulario:', data);

      const success = await updateLesson(lessonId, data, queryClient);

      if (success) {
        toast({
          title: "Éxito",
          description: "La lección se ha actualizado correctamente",
        });
        navigate('/admin/lessons');
      }
    } catch (error) {
      console.error('Error al actualizar la lección:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la lección",
        variant: "destructive",
      });
    }
  };

  return (
    <AdminLayout>
      <div className="container max-w-4xl py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Editar Lección</h1>
          <Button variant="outline" onClick={() => navigate('/admin/lessons')}>
            Volver
          </Button>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <LessonFormFields 
              form={form} 
              questions={questions}
              setQuestions={setQuestions}
            />
            
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/lessons')}
              >
                Cancelar
              </Button>
              <Button type="submit">
                Guardar Cambios
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </AdminLayout>
  );
};

export default AdminEditLesson;